import React from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { MoreHorizontal, Plus, Trash2, Edit2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import ItemCard from "./ItemCard";
import { cn } from "../../lib/utils";

const luminanceHex = (hex) => {
  if (!hex) return 0;
  const h = hex.replace("#", "");
  if (h.length !== 6) return 0;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const capitalizeTitle = (s) => {
  if (!s) return "";
  const t = String(s);
  if (t.length <= 1) return t.toUpperCase();
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

function LaneView({
  lane,
  onAddItem,
  onEditItem,
  onRenameLane,
  onDeleteLane,
}) {
  const itemsIds = Array.isArray(lane.items)
    ? lane.items.map((i) => `item:${i.id}`)
    : [];

  const cor = lane.color || "#64748b";
  const textoClaro = luminanceHex(cor) <= 0.55;
  const classeTexto = textoClaro ? "text-white" : "text-slate-900";
  const classeDropdown = textoClaro ? "text-white/80 hover:text-white" : "text-slate-800 hover:text-slate-900";
  const laneNameDisplay = capitalizeTitle(lane.name);

  return (
    <div
      className={cn(
        "flex h-full w-[310px] shrink-0 flex-col overflow-hidden rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.15)]"
      )}
      style={{ backgroundColor: cor }}
    >
      <div className={cn("flex items-center gap-2 p-3", classeTexto)}>
        <span className="text-sm font-semibold truncate">
          {laneNameDisplay}{" "}
          <span className={cn("opacity-80 font-normal", classeTexto)}>
            / {lane.items?.length || 0}
          </span>
        </span>
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-7 w-7",
                  classeDropdown,
                  "hover:bg-black/10"
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRenameLane?.(lane)}>
                <Edit2 className="mr-2 h-4 w-4" />
                Renomear coluna
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onClick={() => onDeleteLane?.(lane)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir coluna
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
          {Array.isArray(lane.items) &&
            lane.items.map((item) => (
              <ItemCard key={item.id} item={item} onEditItem={onEditItem} />
            ))}
        </SortableContext>
      </div>

      <button
        type="button"
        onClick={() => onAddItem?.(lane)}
        className={cn(
          "w-full flex items-center justify-start gap-2 px-3 py-2.5 text-sm font-medium transition-all hover:brightness-110 active:brightness-95",
          classeTexto
        )}
        style={{ backgroundColor: cor }}
      >
        <Plus className="h-4 w-4" />
        Adicionar
      </button>
    </div>
  );
}

export default LaneView;
